import React, { useState } from "react";
import { motion } from "framer-motion";
import { FaHome, FaCheckCircle, FaArrowRight } from "react-icons/fa";
import BgGlassmorphism from "components/BgGlassmorphism/BgGlassmorphism";

import { HowItWorkCallToActionProps } from "data/types";
import { PRESTA_HOW_IT_WORKS } from "data/constants";

const HowItWorkCallToAction: React.FC<HowItWorkCallToActionProps> = ({
  title = PRESTA_HOW_IT_WORKS.title,
  titleHighlight = PRESTA_HOW_IT_WORKS.titleHighlight,
  steps = PRESTA_HOW_IT_WORKS.steps,
  gradientFrom = PRESTA_HOW_IT_WORKS.gradientFrom,
  gradientTo = PRESTA_HOW_IT_WORKS.gradientTo,
  stepCircleColor = "bg-gradient-to-r from-pink-500 to-rose-500",
  bg = "bg-gradient-to-br from-pink-50 via-white to-rose-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900",
}) => {
  const [currentImage, setCurrentImage] = useState(steps[0].image);
  const [activeStep, setActiveStep] = useState(1);

  const handleStepClick = (image: string, stepNumber: number) => {
    setCurrentImage(image);
    setActiveStep(stepNumber);
  };

  // Animation variants for the image
  const imageVariants = {
    initial: { opacity: 0, scale: 0.95, y: 20 },
    animate: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.6 } },
    exit: { opacity: 0, scale: 0.95, y: -20, transition: { duration: 0.3 } },
  };

  // Container animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  // Step animation variants
  const stepVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6 },
    },
  };

  return (
    <div className={`relative ${bg}`}>
      {/* Background Effects */}
      <BgGlassmorphism />
      <div className="absolute -top-6 -left-6 w-72 h-72 bg-gradient-to-br from-pink-200/30 to-rose-200/30 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute -bottom-6 -right-6 w-96 h-96 bg-gradient-to-br from-purple-200/20 to-pink-200/20 rounded-full blur-3xl"></div>

      <div className="container relative px-4 py-16 mx-auto">
        {/* Hero Section */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-pink-50 to-rose-50 rounded-full text-sm font-medium text-pink-600 mb-6">
            <FaHome className="w-4 h-4 mr-2 text-pink-500" />
            Guide étape par étape
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-neutral-900 dark:text-white mb-6">
            {title}{" "}
            <span className="bg-gradient-to-r from-pink-500 to-rose-500 bg-clip-text text-transparent">
              {titleHighlight}
            </span>
          </h2>
          <p className="text-xl text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto leading-relaxed">
            Suivez notre processus simple pour mettre en ligne votre espace événementiel
          </p>
        </motion.div>

        <div className="flex flex-col w-full gap-12 max-w-7xl mx-auto lg:flex-row">
          {/* Image Section */}
          <motion.div
            className="flex-1"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="relative rounded-3xl dark:bg-neutral-800 p-6">
              {/* Decorative Elements */}
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-br from-pink-400/20 to-rose-400/20 rounded-full blur-xl"></div>
              <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-gradient-to-br from-purple-400/15 to-pink-400/15 rounded-full blur-xl"></div>

              <div className="relative">
                <motion.img
                  key={currentImage}
                  src={currentImage}
                  alt="Step image"
                  className="w-full h-auto object-contain rounded-2xl"
                  variants={imageVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                />
              </div>
            </div>
          </motion.div>

          {/* Steps Section */}
          <motion.div className="flex-1" variants={containerVariants} initial="hidden" animate="visible">
            <div className="space-y-6">
              {steps.map((step, index) => (
                <motion.div
                  key={step.number}
                  className={`group relative cursor-pointer transition-all duration-500 ${
                    activeStep === step.number ? "transform scale-105" : ""
                  }`}
                  variants={stepVariants}
                  onClick={() => handleStepClick(step.image, step.number)}
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <div
                    className={`relative dark:bg-neutral-800 rounded-3xl p-6 transition-all duration-500 ${
                      activeStep === step.number ? "transform scale-105" : ""
                    }`}
                  >
                    {/* Background Effect for Active Step */}
                    {activeStep === step.number && (
                      <div className="absolute inset-0 bg-gradient-to-br from-pink-50/50 to-rose-50/50 dark:from-pink-900/10 dark:to-rose-900/10 rounded-3xl"></div>
                    )}

                    <div className="relative flex items-start">
                      {/* Step Circle */}
                      <div
                        className={`flex items-center justify-center w-16 h-16 mr-6 text-white rounded-2xl transition-all duration-500 ${stepCircleColor} group-hover:scale-110`}
                      >
                        {activeStep === step.number ? (
                          <FaCheckCircle className="w-8 h-8" />
                        ) : (
                          <span className="text-xl font-bold">{step.number}</span>
                        )}
                      </div>

                      <div className="flex-1 space-y-3">
                        {/* Step Title */}
                        <h3
                          className={`text-xl font-bold transition-colors duration-300 ${
                            activeStep === step.number
                              ? "text-pink-600 dark:text-pink-400"
                              : "text-neutral-800 dark:text-neutral-200 group-hover:text-pink-600"
                          }`}
                        >
                          {step.title}
                        </h3>

                        {/* Step Descriptions */}
                        <div className="space-y-2">
                          {step.description.map((desc, descIndex) => (
                            <p key={descIndex} className="text-neutral-600 dark:text-neutral-400 leading-relaxed">
                              {desc}
                            </p>
                          ))}
                        </div>
                      </div>

                      {/* Arrow Icon */}
                      <div
                        className={`flex items-center justify-center w-8 h-8 transition-all duration-300 ${
                          activeStep === step.number
                            ? "text-pink-500 transform translate-x-2"
                            : "text-neutral-400 group-hover:text-pink-500 group-hover:translate-x-1"
                        }`}
                      >
                        <FaArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Call to Action */}
            <motion.div
              className="mt-12 text-center"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
            >
              <div className="bg-gradient-to-r from-pink-500 via-pink-600 to-rose-500 rounded-3xl p-6 text-white">
                <h4 className="text-xl font-bold mb-3">Prêt à commencer ?</h4>
                <p className="mb-4 opacity-90">Ajoutez votre espace dès maintenant</p>
                <button className="text-pink-600 px-6 py-3 rounded-full font-semibold hover:scale-105 transform transition-all duration-300">
                  Ajouter mon espace
                </button>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default HowItWorkCallToAction;
